import apiService from './api';
import type { ApiResponse } from '../types';

/**
 * MYOB AccountRight per-organisation integration client.
 *
 * The Xero Payroll equivalent lives in `xeroPayroll.ts` and follows this same
 * shape; keep the two in step when changing the connection lifecycle.
 */

export interface MyobStatus {
  connected: boolean;
  company_file_selected: boolean;
  myob_user_name: string | null;
  company_file_id?: string | null;
  company_file_uri?: string | null;
  company_file_name: string | null;
  company_file_username?: string | null;
  connected_at: string | null;
  last_export_at: string | null;
  last_export_status: 'success' | 'partial' | 'failed' | null;
  last_export_error: string | null;
}

export interface MyobCompanyFile {
  id: string;
  name: string;
  uri: string;
  country?: string;
  product_level?: string;
}

class MyobService {
  async getStatus(): Promise<ApiResponse<MyobStatus>> {
    return apiService.get('/myob/status');
  }

  async getAuthorizationUrl(): Promise<ApiResponse<{ authorization_url: string }>> {
    return apiService.get('/myob/authorize');
  }

  /**
   * Finish the sign-in MYOB sent the browser back from (`useOAuthReturn`).
   * Only the person who started it, in the organisation they started it for.
   */
  async completeSignIn(code: string, state: string): Promise<ApiResponse<MyobStatus>> {
    return apiService.post('/myob/complete', { code, state }, undefined, { timeout: 30_000 });
  }

  async disconnect(): Promise<ApiResponse<{ message: string }>> {
    return apiService.post('/myob/disconnect', {});
  }

  async listCompanyFiles(): Promise<ApiResponse<MyobCompanyFile[]>> {
    return apiService.get('/myob/company-files');
  }

  /**
   * The company file is chosen once; saving the same one again updates its
   * sign-in details. The server looks the file up by `id` and ignores `uri`
   * and `name`, which are still sent for a server from before it did.
   */
  async setCompanyFile(payload: {
    id: string;
    uri: string;
    name: string;
    username: string;
    password: string;
  }): Promise<ApiResponse<{ message: string }>> {
    return apiService.put('/myob/company-file', payload);
  }
}

export const myobService = new MyobService();
export default myobService;
